import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ChevronLeft, Shield, Lock, User, Mail, Loader2, Monitor, Trash2, Key, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UserSettings() {
    const { user, logout } = useAuth();
    const [twoFAEnabled, setTwoFAEnabled] = useState(user?.two_factor_enabled || false);
    const [saving, setSaving] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(true);
    
    // Audit logs (admin only)
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [logsTotal, setLogsTotal] = useState(0);
    
    // Password change
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        loadSessions();
        if (user?.role === 'admin') {
            loadAuditLogs();
        }
    }, [user]);

    async function loadSessions() {
        try {
            const res = await axios.get(`${API_URL}/api/auth/sessions`);
            setSessions(res.data);
        } catch (e) {
            console.error('Could not load sessions');
        } finally {
            setLoadingSessions(false);
        }
    }

    async function loadAuditLogs() {
        try {
            const res = await axios.get(`${API_URL}/api/admin/audit-logs?limit=10`);
            setAuditLogs(res.data.logs || []);
            setLogsTotal(res.data.total || 0);
        } catch (e) {
            console.error('Could not load audit logs');
        } finally {
            setLoadingLogs(false);
        }
    }

    async function toggle2FA() {
        setSaving(true);
        const newValue = !twoFAEnabled;
        try {
            await axios.post(`${API_URL}/api/auth/2fa/setup`, { enabled: newValue });
            setTwoFAEnabled(newValue);
            toast.success(newValue 
                ? 'Two-Factor Authentication enabled! You will receive a code via email on your next login.' 
                : 'Two-Factor Authentication disabled.'
            );
        } catch (e) {
            toast.error('Failed to update 2FA settings');
        } finally {
            setSaving(false);
        }
    }

    async function terminateSession(sessionId) {
        try {
            await axios.delete(`${API_URL}/api/auth/sessions/${sessionId}`);
            setSessions(sessions.filter(s => s.session_id !== sessionId));
            toast.success('Session terminated');
        } catch (e) {
            toast.error('Failed to terminate session');
        }
    }

    async function terminateAllSessions() {
        try {
            await axios.delete(`${API_URL}/api/auth/sessions`);
            toast.success('All other sessions terminated');
            loadSessions();
        } catch (e) {
            toast.error('Failed to terminate sessions');
        }
    }

    async function changePassword() {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        
        setChangingPassword(true);
        try {
            await axios.post(`${API_URL}/api/auth/change-password`, {
                current_password: currentPassword,
                new_password: newPassword
            });
            toast.success('Password changed successfully');
            setShowPasswordChange(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    }

    function formatDate(dateStr) {
        if (!dateStr) return 'Unknown';
        return new Date(dateStr).toLocaleString();
    }

    function formatAction(action) {
        const actionMap = {
            'login_success': { text: 'Login', color: 'bg-green-100 text-green-700' },
            'login_failed': { text: 'Failed Login', color: 'bg-red-100 text-red-700' },
            'logout': { text: 'Logout', color: 'bg-stone-100 text-stone-700' },
            'register': { text: 'Registration', color: 'bg-blue-100 text-blue-700' },
            'register_with_invite': { text: 'Invited User', color: 'bg-blue-100 text-blue-700' },
            'password_changed': { text: 'Password Changed', color: 'bg-amber-100 text-amber-700' },
            'session_terminated': { text: 'Session Ended', color: 'bg-orange-100 text-orange-700' },
            'all_sessions_terminated': { text: 'All Sessions Ended', color: 'bg-orange-100 text-orange-700' },
            'login_2fa_pending': { text: '2FA Pending', color: 'bg-purple-100 text-purple-700' },
            'login_2fa_success': { text: '2FA Verified', color: 'bg-green-100 text-green-700' },
            '2fa_failed': { text: '2FA Failed', color: 'bg-red-100 text-red-700' },
            'login_blocked_ip': { text: 'IP Blocked', color: 'bg-red-100 text-red-700' },
        };
        return actionMap[action] || { text: action.replace(/_/g, ' '), color: 'bg-stone-100 text-stone-700' };
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            {/* Header */}
            <header className="bg-white border-b border-stone-200 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/dashboard">
                            <Button variant="ghost" size="sm" className="rounded-none">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Back
                            </Button>
                        </Link>
                        <h1 className="font-serif text-xl font-semibold">Account Settings</h1>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-4xl mx-auto p-6 space-y-6">
                {/* Profile Info */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Card className="rounded-none border-stone-200">
                        <CardHeader>
                            <CardTitle className="font-serif text-lg flex items-center gap-2">
                                <User className="h-5 w-5 text-amber-600" />
                                Profile Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
                                    <span className="text-2xl font-serif text-amber-700">
                                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-medium text-lg">{user?.name}</p>
                                    <p className="text-stone-500 flex items-center gap-1">
                                        <Mail className="h-4 w-4" /> {user?.email}
                                    </p>
                                    <Badge variant="outline" className="mt-1 rounded-none capitalize">
                                        {user?.role?.replace('_', ' ')}
                                    </Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Security Settings */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <Card className="rounded-none border-stone-200">
                        <CardHeader>
                            <CardTitle className="font-serif text-lg flex items-center gap-2">
                                <Shield className="h-5 w-5 text-amber-600" />
                                Security Settings
                            </CardTitle>
                            <CardDescription>
                                Manage your account security preferences
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* 2FA - Mandatory */}
                            <div className="flex items-center justify-between p-4 border border-green-200 bg-green-50">
                                <div className="flex items-center gap-3">
                                    <Lock className="h-5 w-5 text-green-600" />
                                    <div>
                                        <Label className="text-base font-medium">Two-Factor Authentication</Label>
                                        <p className="text-sm text-stone-500">
                                            Receive a verification code via email when logging in
                                        </p>
                                    </div>
                                </div>
                                <Badge className="rounded-none bg-green-600">Required</Badge>
                            </div>

                            <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                                <strong>2FA is mandatory for all users.</strong> You will receive a 6-digit code via email each time you log in.
                            </div>

                            {/* Password Change */}
                            <div className="p-4 border border-stone-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Key className="h-5 w-5 text-amber-600" />
                                        <div>
                                            <Label className="text-base font-medium">Change Password</Label>
                                            <p className="text-sm text-stone-500">Update your account password</p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        className="rounded-none"
                                        onClick={() => setShowPasswordChange(!showPasswordChange)}
                                    >
                                        {showPasswordChange ? 'Cancel' : 'Change'}
                                    </Button>
                                </div>
                                
                                {showPasswordChange && (
                                    <div className="mt-4 space-y-3 pt-4 border-t">
                                        <div>
                                            <Label className="text-sm">Current Password</Label>
                                            <Input 
                                                type="password" 
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                className="rounded-none mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-sm">New Password</Label>
                                            <Input 
                                                type="password" 
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="rounded-none mt-1"
                                            />
                                            <p className="text-xs text-stone-500 mt-1">
                                                Min 8 chars, uppercase, lowercase, number, special character
                                            </p>
                                        </div>
                                        <div>
                                            <Label className="text-sm">Confirm New Password</Label>
                                            <Input 
                                                type="password" 
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="rounded-none mt-1"
                                            />
                                        </div>
                                        <Button 
                                            onClick={changePassword}
                                            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                                            className="rounded-none"
                                        >
                                            {changingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                            Update Password
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Session Security Info */}
                            <div className="p-4 border border-stone-200">
                                <h4 className="font-medium mb-2">Security Policies</h4>
                                <ul className="text-sm text-stone-600 space-y-1">
                                    <li>• Sessions expire after <strong>30 minutes</strong> of inactivity</li>
                                    <li>• Account locks after <strong>5 failed</strong> login attempts (30 min lockout)</li>
                                    <li>• Passwords require 8+ chars, uppercase, lowercase, number, special char</li>
                                    <li>• Sensitive data (SSN) is encrypted at rest</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Active Sessions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                >
                    <Card className="rounded-none border-stone-200">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                                        <Monitor className="h-5 w-5 text-amber-600" />
                                        Active Sessions
                                    </CardTitle>
                                    <CardDescription>Manage your logged-in devices</CardDescription>
                                </div>
                                {sessions.length > 1 && (
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        className="rounded-none text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={terminateAllSessions}
                                    >
                                        End All Other Sessions
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loadingSessions ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                                </div>
                            ) : sessions.length === 0 ? (
                                <p className="text-stone-500 text-center py-4">No active sessions found</p>
                            ) : (
                                <div className="space-y-3">
                                    {sessions.map((session, index) => (
                                        <div key={session.session_id} className="flex items-center justify-between p-3 border border-stone-200 bg-stone-50">
                                            <div>
                                                <p className="font-medium text-sm flex items-center gap-2">
                                                    {session.user_agent || 'Unknown Device'}
                                                    {index === 0 && <Badge className="rounded-none text-xs">Current</Badge>}
                                                </p>
                                                <p className="text-xs text-stone-500">
                                                    IP: {session.ip_address || 'Unknown'} • Started: {formatDate(session.created_at)}
                                                </p>
                                            </div>
                                            {index !== 0 && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    className="text-red-600 hover:bg-red-50"
                                                    onClick={() => terminateSession(session.session_id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Audit Logs (Admin Only) */}
                {user?.role === 'admin' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.25 }}
                    >
                        <Card className="rounded-none border-stone-200">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                                            <FileText className="h-5 w-5 text-amber-600" />
                                            Security Audit Log
                                        </CardTitle>
                                        <CardDescription>
                                            Recent security events ({logsTotal} total)
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingLogs ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                                    </div>
                                ) : auditLogs.length === 0 ? (
                                    <p className="text-stone-500 text-center py-4">No audit logs found</p>
                                ) : (
                                    <div className="space-y-2 max-h-80 overflow-y-auto">
                                        {auditLogs.map((log) => {
                                            const actionInfo = formatAction(log.action);
                                            return (
                                                <div key={log.id} className="flex items-center justify-between p-3 border border-stone-200 bg-stone-50">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <Badge className={`rounded-none text-xs ${actionInfo.color}`}>
                                                                {actionInfo.text}
                                                            </Badge>
                                                            <span className="text-sm font-medium truncate">{log.user_email}</span>
                                                        </div>
                                                        <p className="text-xs text-stone-500 mt-1">
                                                            IP: {log.ip_address || 'Unknown'} • {formatDate(log.timestamp)}
                                                        </p>
                                                    </div>
                                                    {log.action.includes('failed') || log.action.includes('blocked') ? (
                                                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Logout */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                >
                    <Card className="rounded-none border-red-200">
                        <CardContent className="py-4">
                            <Button 
                                variant="destructive" 
                                className="rounded-none"
                                onClick={logout}
                                data-testid="logout-btn"
                            >
                                Sign Out
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
}

export default UserSettings;
